/* eslint-disable jsdoc/require-file-overview -- When touching please add documentation. */
import { Either, Just, Maybe, Nothing } from 'purify-ts';
import { getStoreBuilder } from 'vuex-typex';
import { eitherAsync, sort } from '@codegrade/utils';
import client from '@/client';
import * as models from '@/models';
import * as utils from '@/utils';
const storeBuilder = getStoreBuilder();
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
const makeInitialState = () => ({
    allTenantIds: Nothing,
    tenantsLookup: {},
    statistics: {},
    loaders: {
        allTenantIds: Nothing,
        singleTenant: {},
        statistics: {},
    },
});
const moduleBuilder = storeBuilder.module('tenants', makeInitialState());
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export var TenantsStore;
(function (TenantsStore) {
    /**
     * Get the tenant with the given id if we have retrieved it.
     *
     * @param state The current state of the TenantsStore.
     * @returns A function to retrieve a tenant by id.
     */
    // eslint-disable-next-line no-underscore-dangle
    function _getTenant(state) {
        return (tenantId) => Maybe.fromNullable(state.tenantsLookup[tenantId]);
    }
    /**
     * Get the tenant with the given id if we have retrieved it.
     *
     * Note: Using TenantsStore.loadTenants() could not be sufficient, as this
     * will not retrieve hidden tenants if the user is not allowed to see them.
     *
     * @param state The current state of the TenantsStore.
     * @returns A function to retrieve a tenant by id.
     */
    TenantsStore.getTenant = moduleBuilder.read(_getTenant, 'getTenant');
    /* eslint-disable-next-line no-underscore-dangle, jsdoc/require-jsdoc -- When touching please add documentation. */
    function _getStats(state) {
        return (tenant) => Maybe.fromNullable(state.statistics[tenant.id]);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    TenantsStore.getStatistics = moduleBuilder.read(_getStats, 'getStatistics');
    /**
     * Get all of the sorted tenants that are listable by the user.
     *
     * @param state The current state of the Tenant store.
     * @returns All of the listeable tenants sorted by orderCategory then name.
     */
    function sortedTenants(state) {
        return state.allTenantIds
            .map(ids => ids.map(id => state.tenantsLookup[id]))
            .filter(tenant => tenant != null)
            .map(unsorted => sort.sortBy(unsorted, tenant => [tenant.orderCategory, tenant.name], {
            reversePerKey: [true, false],
        }));
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    TenantsStore.allTenants = moduleBuilder.read(sortedTenants, 'allTenants');
    const commitTenantsLoader = moduleBuilder.commit(
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    (state, payload) => {
        state.loaders.allTenantIds = Just(payload.loader);
    }, 'commitTenantsLoader');
    const commitSingleTenantLoader = moduleBuilder.commit(
    /**
     * Commit the loader for a single tenant.
     *
     * @param state The current state of the TenantsStore.
     * @param payload The id of the single tenant we are loading, and the loader.
     */
    (state, payload) => {
        utils.vueSet(state.loaders.singleTenant, payload.id, payload.loader);
    }, 'commitSingleTenantLoader');
    const commitStatsLoader = moduleBuilder.commit(
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    (state, payload) => {
        utils.vueSet(state.loaders.statistics, payload.tenant.id, payload.loader);
    }, 'commitStatsLoader');
    const commitSingleTenant = moduleBuilder.commit(
    /**
     * Commit a single tenant into the store.
     *
     * @param state The current state of the TenantsStore.
     * @param payload The tenant that we want to commit into the store.
     */
    (state, payload) => {
        const singleTenant = payload.singleTenant;
        utils.vueSet(state.tenantsLookup, singleTenant.id, singleTenant);
    }, 'commitSingleTenant');
    const commitTenants = moduleBuilder.commit(
    /**
     * Commit a list of tenants into the store.
     *
     * Note that we keep track of the list of all tenant ids, so that we
     * can use that for listing them.
     *
     * @param state The current state of the TenantsStore.
     * @param payload The list of tenants that we wish to commit.
     */
    (state, payload) => {
        const tenantIds = payload.tenants.map(t => t.id);
        utils.vueSet(state, 'allTenantIds', Just(tenantIds));
        payload.tenants.forEach(tenant => {
            utils.vueSet(state.tenantsLookup, tenant.id, tenant);
        });
    }, 'commitTenants');
    const commitStats = moduleBuilder.commit(
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    (state, payload) => {
        utils.vueSet(state.statistics, payload.tenantId, payload.stats);
    }, 'commitStats');
    /**
     * Method to load all tenants into the store. It is done without the statistics.
     * Those are loaded separately per tenant once it's been opened to avoid unnecessary slowdown.
     */
    TenantsStore.loadTenants = moduleBuilder.dispatch(async (ctx, { force = false, } = {}) => {
        const { loaders, allTenantIds } = ctx.state;
        const loader = loaders.allTenantIds;
        /**
         * Requests the list of all tenants from the backend and commits the
         * result to the tenants store.
         */
        const load = async () => {
            const promise = eitherAsync
                .toPromise(client.tenant.getAll({ withStatistics: false }))
                .then(({ cgResult }) => {
                commitTenants({ tenants: cgResult.map(models.Tenant.fromServerData) });
            });
            commitTenantsLoader({ loader: promise });
        };
        return await utils.loadWithMaybeForce(allTenantIds, loader, force, load, value => Promise.resolve(value));
    }, 'loadTenants');
    /** Load a single tenant, used for targeted loading, when we do not want to list tenants. */
    TenantsStore.loadSingleTenant = moduleBuilder.dispatch(async (ctx, payload) => {
        await TenantsStore.loadTenants({});
        const { loaders, tenantsLookup } = ctx.state;
        const force = payload.force ?? false;
        const tenantId = payload.tenantId;
        const loader = Maybe.fromNullable(loaders.singleTenant[tenantId]);
        /**
         * Requests the list of all tenants from the backend and commits the
         * result to the tenants store.
         */
        const load = async () => {
            const promise = eitherAsync
                .toPromise(client.tenant.get({ tenantId }))
                .then(({ cgResult }) => {
                commitSingleTenant({
                    singleTenant: models.Tenant.fromServerData(cgResult),
                });
            });
            commitSingleTenantLoader({ id: tenantId, loader: promise });
        };
        const maybeTenant = Maybe.fromNullable(tenantsLookup[tenantId]);
        return utils.loadWithMaybeForce(maybeTenant, loader, force, load, () => Promise.resolve());
    }, 'loadSingleTenant');
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    TenantsStore.loadTenantStatistics = moduleBuilder.dispatch(
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    async (ctx, payload) => {
        const { force = false, tenant } = payload;
        const statistics = Maybe.fromNullable(ctx.state.statistics[tenant.id]);
        const loader = Maybe.fromNullable(ctx.state.loaders.statistics[tenant.id]);
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        const load = async () => {
            const statsLoader = client.tenant.getStats({ tenantId: tenant.id }).run();
            commitStatsLoader({ loader: statsLoader, tenant });
            (await statsLoader).ifRight(({ body }) => {
                const stats = models.TenantStatistics.fromServerData(body);
                commitStats({ tenantId: tenant.id, stats });
            });
            return await statsLoader;
        };
        return await utils.loadWithMaybeForce(statistics, loader, force, load, value => Promise.resolve(Either.of(value)));
    }, 'loadTenantStatistics');
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    TenantsStore.addOrUpdateTenant = moduleBuilder.dispatch(
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    async (ctx, payload) => {
        const res = await TenantsStore.loadTenants({ force: false });
        const newTenant = payload.tenant;
        commitTenants({
            tenants: [
                ...sortedTenants(ctx.state)
                    .orDefault([])
                    .filter(tenant => tenant.id !== newTenant.id),
                newTenant,
            ],
        });
        return res;
    }, 'addOrUpdateTenant');
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    TenantsStore.patchTenant = moduleBuilder.dispatch(
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    async (_, { tenant, data }) => await client.tenant
        .patch({
        tenantId: tenant.id,
        requestBody: data,
    })
        .map(res => res.addCallbacks({
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        onAfterSuccess() {
            TenantsStore.addOrUpdateTenant({ tenant: models.Tenant.fromServerData(this.body) });
        },
    }))
        .run(), 'patchTenant');
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    TenantsStore.createTenant = moduleBuilder.dispatch(
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    (_, { data }) => client.tenant.create({ requestBody: data }).map(res => res
        .map(body => models.Tenant.fromServerData(body))
        .addCallbacks({
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        onAfterSuccess() {
            TenantsStore.addOrUpdateTenant({ tenant: this.cgResult });
        },
    })), 'createTenant');
})(TenantsStore || (TenantsStore = {}));
